import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams, IDatasource, IGetRowsParams } from 'ag-grid-community';
import { CommonModule } from '@angular/common';

import { FormsModule } from '@angular/forms';
import { AgGridModule } from 'ag-grid-angular';

import { ApiUrlService } from 'src/app/services/api-url.service';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { DataService } from '../../services/data.service';
import { LoadingService } from '../../services/loading.service';

import { BatchDeltasColDefs } from 'src/app/models/batch-deltas-col-defs.model';
import { ActionsCellRendererComponent } from 'src/app/shared/ui/actions-cell-renderer.component';
import { AgPaginationToolbarComponent } from 'src/app/shared/ui/ag-pagination-toolbar/ag-pagination-toolbar.component';

import { formatGridValueOrNA, getAgGridFilterType } from 'src/app/shared/ui/grid-format.util';
import { UiModule } from 'src/app/shared/ui/ui.module';
import { PayloadDialogComponent } from './payload-dialog.component';
import { TransmissionStatusCellRendererComponent } from '../../shared/ui/transmission-status-cell-renderer.component';
import { TransmissionStatusFilterComponent } from '../../shared/ui/transmission-status-filter.component';

import { environment } from 'src/environments/environment';

@Component({
  standalone: true,
    selector: 'app-ema-batch-view-deltas',
    imports: [CommonModule, FormsModule, AgGridModule, UiModule,AgPaginationToolbarComponent],
    templateUrl: './ema-batch-view-deltas.component.html',
    styleUrls: ['./ema-batch-view-deltas.component.scss']
})
export class EmaBatchViewDeltasComponent implements OnInit, OnDestroy {
  batchId: string | null = null;
  rowData: any[] = [];
  totalRowCount = 0;
  currentPage = 1;
  startDate?: Date;
  endDate?: Date;

  gridApi!: GridApi;
  gridOptions: any;
  columnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: false
  };
  materialCrumbs = [
    { label: 'Home', link: '/' },
    { label: 'Batch Headers', link: null } // Current page, no link
  ];

  gridContext = { componentParent: this };
  private isTabActivated = false;
  private isNavigatingToPage = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public loadingService: LoadingService,
    private dataService: DataService,
    private apiService: ApiService,
    private authService: AuthService,
    private dialog: MatDialog,
    private http: HttpClient,
    private apiUrlService: ApiUrlService
  ) { }

  ngOnInit() {
    this.batchId = this.route.snapshot.paramMap.get('batchId');
    this.setupGrid();
    window.addEventListener('resize', this.onWindowResize);
    document.addEventListener('viewPayload', (e: any) => this.viewPayload(e.detail));
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    document.removeEventListener('viewPayload', (e: any) => this.viewPayload(e.detail));
  }

  private safeGridApiCall(callback: (api: GridApi) => void): void {
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      callback(this.gridApi);
    }
  }

  // Helper function to convert a Date object or date string into "YYYY-MM-DD" format
  private toYmd(d: string | Date): string {
    const date = typeof d === 'string' ? new Date(d) : d; // Convert string to Date if needed
    const y = date.getFullYear(); // Get full year (e.g., 2025)
    const m = String(date.getMonth() + 1).padStart(2, '0'); // Get month (1–12) and pad with zero
    const day = String(date.getDate()).padStart(2, '0'); // Get day and pad with zero
    return `${y}-${m}-${day}`; // Return formatted date string
  }


  // Builds query parameters from AG Grid filters for backend API calls
  private buildQueryFromAgFilters(
    filterModel: any,
    colDefs: { field: string; filterPath?: string }[]
  ): URLSearchParams {
    const params = new URLSearchParams();
    const pathFor = (field: string) =>
      colDefs.find(c => c.field === field)?.filterPath || field;

    // Helper: next day in YYYY-MM-DD (treats input as local date)
    const nextDayYmd = (ymd: string) => {
      const d = new Date(`${ymd}T00:00:00`);
      d.setDate(d.getDate() + 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    // Helper: normalize a YYYY-MM-DD range; if reversed, swap.
    const normalizeRange = (a?: string, b?: string) => {
      if (!a && !b) return { from: undefined, to: undefined };
      if (!a) return { from: undefined, to: b };
      if (!b) return { from: a, to: undefined };
      return a <= b ? { from: a, to: b } : { from: b, to: a };
    };

    Object.entries(filterModel || {}).forEach(([field, m]: [string, any]) => {
      const fp = pathFor(field);
      if (!fp) return;

      if (m.filterType === 'date') {
        const type = m.type as 'equals' | 'notEqual' | 'lessThan' | 'greaterThan' | 'inRange';
        const rawFrom = m.dateFrom ? this.toYmd(m.dateFrom) : undefined;
        const rawTo = m.dateTo ? this.toYmd(m.dateTo) : undefined;
        const { from, to } = normalizeRange(rawFrom, rawTo);

        switch (type) {
          case 'equals': {
            // Match any time on that calendar day → send a same-day range [from, nextDay).
            // Ex: Transaction__End__gte=2025-10-01 & Transaction__End__lt=2025-10-02
            if (from) {
              params.set(`${fp}__gte`, from);
              params.set(`${fp}__lt`, nextDayYmd(from));
            }
            break;
          }

          case 'notEqual': {
            // Exclude exactly that date (backend handles equality semantics).
            // Ex: Transaction__End__ne=2025-10-01
            if (from) params.set(`${fp}__ne`, from);
            break;
          }

          case 'lessThan': {
            // Strictly before the start of that date (no values on that day).
            // Ex: Transaction__End__lt=2025-10-01
            if (from) params.set(`${fp}__lt`, from);
            break;
          }

          case 'greaterThan': {
            // Strictly after the start of that date (does not include that day).
            // Ex: Transaction__End__gt=2025-10-01
            if (from) params.set(`${fp}__gt`, from);
            break;
          }

          case 'inRange': {
            // Inclusive bounds on dates (covers both endpoints as dates).
            // Ex: Transaction__End__gte=2025-10-01 & Transaction__End__lte=2025-10-15
            if (from) params.set(`${fp}__gte`, from);
            if (to) params.set(`${fp}__lte`, to);
            break;
          }
        }
       } else {
        const moneyPaths = ['Terms__WSP__Money__value', 'Terms__WSP__Money___currency'];
        const isMoney = moneyPaths.includes(fp);

        switch (m.type) {
          case 'equals':
            // Exact text match.
            params.set(fp, m.filter);
            break;
          case 'notEqual':
            // Text inequality. Ex: Transaction__LicenseType__ne=EST
            params.set(`${fp}__ne`, m.filter);
            break;
          case 'contains':
            // Case-insensitive substring.
            params.set(`${fp}__icontains`, m.filter);
            break;
          case 'notContains':
            if (isMoney) params.set(`${fp}__not_icontains`, m.filter);
            break;
          case 'startsWith':
            if (isMoney) params.set(`${fp}__istartswith`, m.filter);
            break;
          case 'endsWith':
            if (isMoney) params.set(`${fp}__iendswith`, m.filter);
            break;
          case 'blank':
            if (isMoney) params.set(`${fp}__blank`, 'true');
            break;
          case 'notBlank':
            if (isMoney) params.set(`${fp}__not_blank`, 'true');
            break;
        }
      }
    });

    return params;
  }


  setupGrid(): void {

    // Remove custom filterpath from BatchDeltasColDefs before assigning to grid
    const strippedColDefs = BatchDeltasColDefs.map(({ filterPath, ...colDef }) => colDef);

    this.columnDefs = [
      {
        headerName: "",
        maxWidth: 20,
        // it is important to have node.id here, so that when the id changes (which happens
        // when the row is loaded) then the cell is refreshed.
        valueGetter: "node.id",
        cellRenderer: (params: ICellRendererParams) => {
          if (params.value !== undefined) {
            return "";
          } else {
            return '<img src="https://www.ag-grid.com/example-assets/loading.gif">';
          }
        },
      },
      {
        headerName: 'Actions',
        field: 'actions',
        cellRenderer: ActionsCellRendererComponent,
        width: 100,
        cellClass: 'no-focus-outline',
      },
      ...strippedColDefs.map(col => {
        if (col.field === 'Transmission Status') {
          // Preserve custom filter configuration for Transmission Status
          return {
            ...col,
            lockPosition: ['left', 'right'].includes(col.lockPosition ?? '')
              ? col.lockPosition as 'left' | 'right'
              : undefined,
            tooltipValueGetter: (params: any) => {
              const map: { [key: number]: string } = { 0: 'Waiting', 1: 'Success', 2: 'Failed' };
              const val = params.value;
              if (typeof val === 'number') 
                return map[val] || String(val);
              return val ?? 'N/A';
            },
            floatingFilter: false,
            valueFormatter: col.cellRenderer ? undefined : formatGridValueOrNA, // Don't format if using custom renderer
          };
        }
        const mapped: ColDef = {
          ...col,
          lockPosition: ['left', 'right'].includes(col.lockPosition ?? '')
            ? (col.lockPosition as 'left' | 'right')
            : undefined,
          tooltipValueGetter: formatGridValueOrNA,
          filter: getAgGridFilterType(col.cellDataType),
          floatingFilter: false,
          valueFormatter: col.cellRenderer ? undefined : formatGridValueOrNA, // Don't format if using custom renderer
          cellRenderer: col.cellRenderer === 'TransmissionStatusCellRendererComponent'
            ? TransmissionStatusCellRendererComponent
            : col.cellRenderer,
          filterParams: {
            maxNumConditions: 1,
            ...col.filterParams // Preserve any existing filterParams
          },
          sortable: col.sortable ?? true,
          resizable: true,
          flex: col.flex,
          minWidth: col.minWidth,
        };

        // Coerce empty string to null for date columns so AG Grid compares correctly
        if (col.cellDataType === 'date') {
          const originalGetter = col.valueGetter;
          mapped.valueGetter = (p: any) => {
            const v = originalGetter ? originalGetter(p) : (p?.data?.[col.field as string]);
            return (v === '' || v === undefined) ? null : v;
          };
        }

        return mapped;
      })
    ];

    this.gridOptions = {
      rowModelType: 'infinite',
      pagination: true,
      paginationPageSize: 25,
      paginationPageSizeSelector: false, // Disable AG Grid's built-in pagination selector since we use custom toolbar
      cacheBlockSize: 25,
      context: this.gridContext,
      suppressColumnVirtualisation: true, // Disable column virtualization for autoSizeAllColumns to work on all columns
      onFirstDataRendered: (params: any) => {
        // Auto-size all columns to content width once data is rendered
        this.safeGridApiCall(api => {
          api.autoSizeAllColumns();
        });
      },
      dataTypeDefinitions: {
        string: {
          baseDataType: 'text',
          extendsDataType: 'text',
          valueParser: (params: any) => params.newValue,
          valueFormatter: (params: any) => params.value
        },
        integer: {
          baseDataType: 'number',
          extendsDataType: 'number',
          valueParser: (params: any) => parseInt(params.newValue),
          valueFormatter: (params: any) => params.value
        },
        date: {
          baseDataType: 'date',
          extendsDataType: 'date',
          valueParser: (params: any) => new Date(params.newValue),
          valueFormatter: (params: any) => params.value
        }
      },
      components: {
        TransmissionStatusCellRendererComponent: TransmissionStatusCellRendererComponent,
        transmissionStatusFilter: TransmissionStatusFilterComponent

      }
    };
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.safeGridApiCall(api => api.sizeColumnsToFit());

    const datasource: IDatasource = {
      getRows: (params: IGetRowsParams) => {
        const page = Math.floor(params.startRow / this.gridOptions.cacheBlockSize) + 1;
        this.fetchBatchDeltasData(page, params, params.sortModel, params.filterModel);
      }
    };

    // Use the new API instead of deprecated setDatasource
    this.safeGridApiCall(api => api.updateGridOptions({ datasource }));
  }

  fetchBatchDeltasData(page: number, params: IGetRowsParams, sortModel: any = [], filterModel: any = {}): void {
    if (!this.batchId) return;

    // Create a lookup map for column definitions
    const columnDefMap = new Map();
    BatchDeltasColDefs.forEach(colDef => {
      columnDefMap.set(colDef.field, colDef);
    });

    // Build sort query using filterPath and proper format
    const sortQueries = sortModel.map((s: any) => {
      const colDef = columnDefMap.get(s.colId);
      const fieldPath = colDef?.filterPath || s.colId;
      const prefix = s.sort === 'desc' ? '-' : '';
      return `order_by=${prefix}${fieldPath}`;
    });

    const limit = this.gridOptions.cacheBlockSize;

    // ⬇️ Use our mapper that sends __ne/__lt/__gt/__gte/__lte and honors filterPath
    const queryParams = this.buildQueryFromAgFilters(filterModel, BatchDeltasColDefs);
    queryParams.append('page', String(page));
    queryParams.append('limit', String(limit));
    // Add sort parameters
    sortQueries.forEach((sortQuery: { split: (arg0: string) => [any, any]; }) => {
      const [key, value] = sortQuery.split('=');
      queryParams.append(key, value);
    });

    const queryString = queryParams.toString();

    const endpoint = this.apiUrlService.getUrl(
      'gatewayBaseUrl',                        // serviceKey from environment.apiBaseUrls
      'batchDelta',                            // endpointKey from environment.apiEndpoints
      { batchId: this.batchId! },              // pathParams (replace :batchId in the endpoint)
      queryString                              // query string
    );
    console.log('Full endpoint URL:', endpoint);
    this.rowData = [];

    this.apiService.getRequest(endpoint).subscribe({
      next: (response: any) => {
        const items = response.items || [];
        this.totalRowCount = response.total || 0;

        // Only update currentPage if we're not navigating to a specific page
        if (!this.isNavigatingToPage) {
          this.currentPage = response.page ?? 1;
        } else {
          // Reset the flag after navigation
          this.isNavigatingToPage = false;
        }

        if (items.length === 0) {
          params.successCallback([], 0);
          this.safeGridApiCall(api => api.showNoRowsOverlay());
          return;
        }
        this.safeGridApiCall(api => api.hideOverlay());
        params.successCallback(items, this.totalRowCount);
      },
      error: () => {
        params.failCallback();
        this.rowData = [];
      }
    });
  }

  viewPayload(row: any): void {
    const payloadData = row._originalData || row;
    this.dialog.open(PayloadDialogComponent, {
      width: '80%',
      maxWidth: '1200px',
      height: '80%',
      data: {
        payload: payloadData,
        title: `Batch Payload Details - ${this.batchId}`,
        batchInfo: row
      },
      panelClass: 'payload-dialog-container'
    });
  }

  applyFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.safeGridApiCall(api => api.setQuickFilter(value.trim().toLowerCase()));
  }

  onPageSizeChanged(newSize: number): void {
    this.gridOptions.cacheBlockSize = newSize;
    this.gridOptions.paginationPageSize = newSize;
    this.currentPage = 1;

    this.safeGridApiCall(api => {
      // Use the new API instead of deprecated paginationSetPageSize
      api.updateGridOptions({
        paginationPageSize: newSize,
        cacheBlockSize: newSize
      });

      // Clear cache and reset datasource
      api.purgeInfiniteCache();

      // Set up datasource again to ensure proper pagination
      const datasource: IDatasource = {
        getRows: (params: IGetRowsParams) => {
          const page = Math.floor(params.startRow / this.gridOptions.cacheBlockSize) + 1;
          this.fetchBatchDeltasData(page, params, params.sortModel, params.filterModel);
        }
      };

      api.updateGridOptions({ datasource });
    });
  }

  onGoToPage(pageNumber: number): void {
    if (!this.isTabActivated) {
      return;
    }

    if (!this.gridApi) {
      return;
    }

    // Set a flag to prevent the fetch method from overriding our currentPage
    this.isNavigatingToPage = true;
    this.currentPage = pageNumber;

    // Try ag-Grid's built-in pagination methods first
    try {
      if ((this.gridApi as any).paginationGoToPage) {
        const targetPage = pageNumber - 1; // ag-Grid uses 0-based indexing
        (this.gridApi as any).paginationGoToPage(targetPage);
        return;
      }
    } catch (e) {
      // Pagination method failed, fall back to infinite scroll approach
      console.error('paginationGoToPage failed:', e);
    }

    // Try ag-Grid's pagination API
    try {
      if ((this.gridApi as any).paginationSetPage) {
        const targetPage = pageNumber - 1; // ag-Grid uses 0-based indexing
        (this.gridApi as any).paginationSetPage(targetPage);
        return;
      }
    } catch (e) {
      // Pagination method failed, fall back to infinite scroll approach
      console.error('paginationSetPage failed:', e);
    }

    // Fallback to infinite scroll approach
    const startRow = (pageNumber - 1) * this.gridOptions.cacheBlockSize;

    // Clear the cache and try to navigate
    this.safeGridApiCall(api => api.purgeInfiniteCache());

    setTimeout(() => {
      this.safeGridApiCall(api => {
        // Reset the datasource
        const datasource: IDatasource = {
          getRows: (params: IGetRowsParams) => {
            const page = Math.floor(params.startRow / this.gridOptions.cacheBlockSize) + 1;
            this.fetchBatchDeltasData(page, params, params.sortModel, params.filterModel);
          }
        };

        api.updateGridOptions({ datasource });

        // Try to navigate after datasource is set
        setTimeout(() => {
          this.safeGridApiCall(innerApi => {
            if (startRow < this.totalRowCount) {
              innerApi.ensureIndexVisible(startRow, 'top');
            }
          });
        }, 300);
      });
    }, 100);
  }

  onCrumbClicked(event: any) {
    if (event.link) {
      this.router.navigate([event.link]);
    }
  }

  public onTabActivated(): void {
    this.isTabActivated = true;
    setTimeout(() => {
      this.safeGridApiCall(api => {
        api.autoSizeAllColumns();
        api.refreshInfiniteCache();
      });
    }, 100);
  }

  onWindowResize = () => {
    this.safeGridApiCall(api => api.autoSizeAllColumns());
  }

  onExport(): void {
    this.safeGridApiCall(api => {
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;

      api.exportDataAsCsv({
        fileName: `batch-${this.batchId}-export-${timestamp}.csv`,
        allColumns: true,
        onlySelected: false,
        skipRowGroups: true,
        processCellCallback: (params) => {
          return params.value != null ? params.value : '';  // Avoid N/A
        },
      });
    });
  }

  onGenerateAvails(): void {
    if (!this.batchId) {
      alert('Batch ID is required for generating avails');
      return;
    }

    // Get user_id from localStorage -> uid as requested
    let userId = localStorage.getItem('uid');

    // Get user email from localStorage (stored by auth service from payload)
    let userEmail = localStorage.getItem('email');

    // FIXME: The SSO system should populate userId/userEmail with defaults when
    // SSO is disabled
    if (!environment.enableSSO) {
      userEmail = environment.batchExport.localUserEmail;
    } else {
      if (!userId) {
        alert('User ID is required for generating avails');
        return;
      }

      if (!userEmail) {
        alert('User email is required for generating avails');
        return;
      }
    }

    const payload = {
      batch_id: this.batchId,
      user_id: userId,
      user_email: userEmail,
    };
    console.log('Generate Avails payload:', payload);

    // TODO: Move endpoint to environment config
    //const endpoint = 'http://localhost:8000/api/v1/export/delta';
    const endpoint = environment.apiBaseUrls.gatewayBaseUrl + "/batch/export/delta"

    this.http.post(endpoint, payload, {
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json'
      }
    }).subscribe({
      next: (response) => {
        console.log('Generate Avails successful:', response);
        alert('Request to Generate the EMA Avails report was generated successfully.  Please check your email for report details.');
      },
      error: (error) => {
        console.error('Generate Avails failed:', error);
        switch (error.status) {
          case 409: // Conflict, export is already running
            alert('This report is currently being generated. Another request is already in progress. Please refresh the page and try again in a few minutes.');
            break;

          case 500:
          default:
            alert('Request failed to Generate the EMA Avails report.  Please contact support.');

        }
      }
    });
  }
}
